import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import {
  ConversionDataListing,
  ConversionListingDataResponse,
  MbrsStatusEnum,
} from '../../../types/commons';
import { map, Observable } from 'rxjs';
import { ToastMessageOptions } from 'primeng/api';
import { RuntimeConfigService } from './runtime-config.service';
import {
  TOAST_DURATION_LONG_MS,
} from '../../utils/toast-durations.util';
import { SKIP_GLOBAL_ERROR_REDIRECT } from '../../utils/http-context-tokens.util';

@Injectable({
  providedIn: 'root',
})
export class ConversionListingService {
  private readonly config = inject(RuntimeConfigService);
  private readonly API_URL = `${this.config.apiUrl()}/mbrs/conversion-listing`;

  constructor(private http: HttpClient) {}

  // function to get status class
  getStatusClass(state: MbrsStatusEnum): string {
    switch (state) {
      case MbrsStatusEnum.Success:
      // AS-842: Success* shares the green Success palette; the asterisk is
      // the only visual differentiator (added in getDisplayStatus).
      case MbrsStatusEnum.SuccessWithWarnings:
        return 'bg-[#c6f0dd] text-green-700';
      case MbrsStatusEnum.Failed:
        return 'bg-[#ffe7ea] text-[#e73a3a]';
      case MbrsStatusEnum.InProgress:
      // PendingValidation is rendered as a skeleton loader by the listing
      // templates (xbrl-files / recent-conversion / debug layout), so this
      // branch is normally not reached for it. The InProgress palette is
      // kept here only as a defensive fallback for any other caller that
      // asks for a PendingValidation class.
      case MbrsStatusEnum.PendingValidation:
        return 'bg-[#E0F2FE] text-[#007DA9]';
      case MbrsStatusEnum.Review:
        return 'bg-[#FFF0CB] text-[#998210]';
      default:
        return '';
    }
  }

  getDisplayStatus(state: MbrsStatusEnum): string {
    // PendingValidation is rendered as a skeleton loader by the listing
    // templates, so its label is normally not shown. The "In Progress"
    // fallback is kept for any non-listing caller — visually it matches the
    // actual InProgress state (see writer-separation rationale).
    if (
      state === MbrsStatusEnum.InProgress ||
      state === MbrsStatusEnum.PendingValidation
    ) {
      return 'In Progress';
    }
    // AS-842: Success* (no errors, ≥1 warning) — green chip with trailing
    // asterisk to differentiate from a clean Success.
    if (state === MbrsStatusEnum.SuccessWithWarnings) {
      return 'Success*';
    }
    return MbrsStatusEnum[state];
  }

  /**
   * The chip label WITHOUT the Success* warning asterisk (e.g. 'Success' for
   * SuccessWithWarnings). Pair with {@link hasWarningAsterisk} so the template
   * can colour the asterisk independently — the trailing `*` must render red
   * (`text-red-500`, matching the "(*)Optional: Suggestions to refine data"
   * legend) instead of inheriting the chip's green text. Everything else
   * mirrors {@link getDisplayStatus}.
   */
  getDisplayBaseStatus(state: MbrsStatusEnum): string {
    if (state === MbrsStatusEnum.SuccessWithWarnings) {
      return 'Success';
    }
    return this.getDisplayStatus(state);
  }

  /** True when the chip should append a red warning asterisk (Success*). */
  hasWarningAsterisk(state: MbrsStatusEnum): boolean {
    return state === MbrsStatusEnum.SuccessWithWarnings;
  }

  /**
   * AS-842 — single source of truth for the *terminal-status toast* wording.
   *
   * Co-located with `getDisplayStatus` / `getStatusClass` (the chip's
   * status→display mapping) on purpose: the Success / Success* / Review
   * distinction now lives in ONE file, so the chip and the toast can't drift.
   * Previously this wording was inlined in
   * `xbrl-files.component.ts → fireTerminalToast`, which made point 2 (a
   * distinct Success* message) a two-place edit.
   *
   * Returns the PrimeNG `Message` to fire, or `null` for any non-terminal
   * status (PendingValidation / InProgress / Failed / AwaitingFiles) — the
   * caller fires nothing on null. `recent-conversion` deliberately does NOT
   * call this (terminal-toast UX is owned by the xbrl-files listing); the
   * method is shared so any future consumer reuses the same wording.
   */
  getTerminalToast(state: MbrsStatusEnum, fileName: string): ToastMessageOptions | null {
    if (state === MbrsStatusEnum.Review) {
      return {
        key: 'process_mbrs_data',
        severity: 'custom-warning',
        summary: `There are some fields that could be incorrect. Please review and make the necessary amendments for ${fileName}.`,
        life: TOAST_DURATION_LONG_MS,
      };
    }

    if (state === MbrsStatusEnum.Success) {
      return {
        key: 'process_mbrs_data',
        severity: 'custom-success',
        summary: `The ${fileName}.xbrl file is ready for download.`,
        life: TOAST_DURATION_LONG_MS,
      };
    }

    if (state === MbrsStatusEnum.SuccessWithWarnings) {
      return {
        key: 'process_mbrs_data',
        severity: 'custom-success',
        // AS-842 (point 2): Success* gets its OWN wording flagging the
        // warnings. To revert to the original Q5 behaviour (Success* shares
        // the plain Success message), comment out the distinct line below and
        // uncomment the shared line instead — the two are kept side-by-side so
        // the swap is a one-line comment toggle.
        // summary: `The ${fileName}.xbrl file is ready for download, but some fields may need review.`,
        summary: `The ${fileName}.xbrl file is ready for download.`,
        life: TOAST_DURATION_LONG_MS,
      };
    }

    return null;
  }

  fetchConversionListing(params?: {
    limit?: number;
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    status?: MbrsStatusEnum | string;
    // `fromDate`/`toDate` filter on the conversion date; `modifiedFromDate`/
    // `modifiedToDate` on the last-modified date. The backend AND-s them and
    // ignores either range unless both of its ends are present.
    fromDate?: string;
    toDate?: string;
    modifiedFromDate?: string;
    modifiedToDate?: string;
    sortBy?: string;
    isDescending?: boolean;
  }): Observable<ConversionDataListing> {
    return this.http
      .get<ConversionListingDataResponse>(this.API_URL, {
        params: { ...params },
        withCredentials: true,
        // See SKIP_GLOBAL_ERROR_REDIRECT's own docblock: this call's failure
        // is classified and toasted by page-load-failure.util.ts (the
        // XBRL_FILES and RECENT_CONVERSION scopes) — the global interceptor's
        // redirect must not win that race.
        context: new HttpContext().set(SKIP_GLOBAL_ERROR_REDIRECT, true),
      })
      .pipe(map((response) => response.data));
  }
}
